<template>
  <section class="form-list">
    <div v-show="isLoading || isSaving || isLoadingError" class="loading-box">
      <div class="loading-main">
        <svg v-show="isLoading || isSaving" class="loading-circular" viewBox="25 25 50 50">
          <circle class="loading-path" cx="50" cy="50" r="20" fill="none"/>
        </svg>
        <div v-show="isLoadingError" class="loading-refresh">
          <i class="el-icon-refresh"/>
        </div>
        <div v-show="isLoadingError" class="loading-text">
          数据加载失败，点击重新尝试
        </div>
      </div>
    </div>

    <el-form
      class="form form-max"
      :class="{ 'is-data-loading': isDataLoading }"
      :model="report"
      ref="form"
      label-position="left"
      label-width="80px"
    >
      <div class="report-toolbar">
        <el-form-item v-if="isAdministrator" label="所属门店">
          <span>{{ report.store_name }}</span>
        </el-form-item>
        <el-form-item label="所属日期">
          <span>{{ report.report_date | timeToYmd }}</span>
        </el-form-item>
      </div>

      <summary-bar
        :income="report.all_income"
        :expense="summaryExpenseTotal"
        :refund="expenseTotal"
        :reserve-pay="report.expenditure || 0"
        :reserve="report.reserve_balance"
      >
        <template v-slot:actions>
          <el-button
            type="primary"
            :loading="isSaving"
            @click="handleSubmit"
          >保存修改</el-button>
          <el-button @click="RouteConfig.dailyReportList.back">返回</el-button>
        </template>
      </summary-bar>

      <div class="report-grid">
      <report-sections
        :report="sectionsReport"
        :list="list"
        :refund-totals="refundTotals"
        :is-administrator="isAdministrator"
        @page="handlePage"
      >
        <template v-slot:income-extra>
          <report-group
            title="其他收入"
            :count="report.other_incomes.length"
            :amount="otherIncomeCents"
            tone="income"
          >
            <div v-if="report.other_incomes.length" class="other-head">
              <span class="other-index">序号</span>
              <span>名称</span>
              <span>来源</span>
              <span>有赞券码</span>
              <span>金额</span>
              <span/>
            </div>
            <div
              v-for="(otherIncome, otherIncomeIndex) in report.other_incomes"
              :key="`other-income-${otherIncomeIndex}`"
              class="other-row"
            >
              <span class="other-index">{{ otherIncomeIndex + 1 }}</span>
              <el-input
                v-model="otherIncome.title"
                class="other-name"
                maxlength="64"
                aria-label="其他收入名称"
                placeholder="收入名称"
              />
              <v-select-card-payment-mode
                v-model="otherIncome.card_payment_mode_id"
                class="other-mode"
                placeholder="收入来源"
                @input="computeOtherIncome"
              />
              <!-- 券码列固定占位：非有赞时内容为空，但列不会塌陷导致后面整体左移 -->
              <div class="other-youzan">
                <v-input-you-zan-number
                  v-model="otherIncome.you_zan_number"
                  :card-payment-mode-id="otherIncome.card_payment_mode_id"
                  @actualPaymentChange="actual_payment => otherIncome.actual_payment = actual_payment"
                />
              </div>
              <v-input-money
                v-model="otherIncome.actual_payment"
                class="other-money"
                aria-label="其他收入金额"
                :max="99999.99"
                :is-show-suffix="true"
                @input="computeOtherIncome"
              />
              <el-button type="text" class="other-del" @click="removeOtherIncome(otherIncomeIndex)">删除</el-button>
            </div>
            <el-button class="other-add" type="text" @click="addOtherIncome">新增一项其他收入</el-button>
          </report-group>
        </template>

        <template v-slot:after-expense>
          <reserve-panel :report="report" @change="handleReserveChange"/>
        </template>
      </report-sections>

      <section class="report-card course-card">
        <div class="report-card__head">
          <h2 class="report-card__title">今日课程出勤</h2>
        </div>
        <div class="report-card__body">
        <el-table
          :data="courses"
          :empty-text="courseEmptyText"
          border
          fit
          class="course-table"
          show-summary
          :summary-method="getTableSummaries"
        >
          <el-table-column label="课程时间" align="center" fixed="left">
            <template v-slot="{ row }">{{ row.start_time | timeToHi }}{{ ' - '}}{{ row.end_time | timeToHi }}</template>
          </el-table-column>

          <el-table-column label="课程时长" align="center" width="100" prop="duration" type="money">
            <template v-slot="{ row }">{{ row.duration | toYuan }}</template>
          </el-table-column>

          <el-table-column label="课程类型" align="center" prop="card_type_name"/>
          <el-table-column label="课程名称" align="center">
            <template v-slot="{ row }">
              <router-link class="primary-color" :to="`/courses/${row.id}/manage`" target="_blank">{{ row.course_name }}</router-link>
            </template>
          </el-table-column>
          <el-table-column label="预约人数" align="center" prop="student_count"/>
          <el-table-column label="签到人数" align="center" prop="signed_count"/>

          <el-table-column label="课程老师 / 出勤状态" min-width="300" align="center">
            <template v-slot="{ row }">
              <div
                class="teacher-item"
                v-for="(teacher, teacherIndex) in row.teachers"
                :key="`teacher${teacherIndex}`"
              >
                <v-avatar-nickname :value="teacher.avatar" :nickname="teacher.nickname"/>
                <el-select
                  class="course-state-select"
                  v-model.number="teacher.course_state"
                  placeholder="请选择出勤状态"
                  filterable
                >
                  <el-option
                    v-for="v in courseStates"
                    :key="`courseState${v.name}`"
                    :label="v.name"
                    :value="v.value"
                  />
                </el-select>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="代课老师 / 出勤状态" min-width="300" align="center">
            <template v-slot="{ row }">
              <div
                class="teacher-item"
                v-for="(teacher, teacherIndex) in row.teachers"
                :key="`subTeacher${teacherIndex}`"
              >
                <v-select-sub-teacher
                  :class="`course-state-select${teacher.course_state === courseStates.leave.value ? '' : ' hidden'}`"
                  v-model="teacher.sub_teacher_id"
                  :list="teachers"
                  :disabledId="[teacher.id]"
                  :remote="true"
                  placeholder="输入代课老师名称进行搜索"
                />
                <el-select
                  :class="`course-state-select${teacher.course_state === courseStates.leave.value ? '' : ' hidden'}`"
                  class="course-state-select"
                  v-model.number="teacher.sub_course_state"
                  placeholder="请选择代课老师出勤状态"
                  filterable
                >
                  <el-option
                    v-for="v in courseStates"
                    :key="`courseStateSub${v.name}`"
                    :label="v.name"
                    :value="v.value"
                    :disabled="v.value === courseStates.leave.value"
                  />
                </el-select>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          v-show="courseCount > 0"
          class="group-pagination"
          :total="courseCount"
        />
        </div>
      </section>
      </div>
    </el-form>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { mapGetters } from 'vuex';
import { courseStates } from '@/enums/courseStates';
import { index as getTeachers } from '@/api/teacher';
import { getDailyCards } from '@/api/card';
import { getIncomes, getCourses, show, update, getRefunds, getUpdatePayments, getReservationIncomes, getReservationPays, getCardTicketIncomes, getVoucherIncomes, getCardTicketRefunds, getVoucherRefunds } from '@/api/daily-report';
import { getDailyTrialPayments } from "@/api/trial-payment";
import { minus, plus, toYuan, toCent, getTableSummaries } from "@/utils/number";
import { paymentModes, findPaymentModeName } from "@/enums/paymentModes";
import SummaryBar from './components/SummaryBar.vue';
import ReportSections from './components/ReportSections.vue';
import ReportGroup from './components/ReportGroup.vue';
import ReservePanel from './components/ReservePanel.vue';

export default {
  name: RouteConfig.dailyReportEdit.name,
  components: { SummaryBar, ReportSections, ReportGroup, ReservePanel },
  data: () => ({
    RouteConfig,
    isLoading: false,
    isCardsLoading: false,
    isLoadingError: false,
    isCardsLoadingError: false,
    isSaving: false,
    listPageSize: 10,
    cardListPageSize: 8,
    trailPaymentsPageSize: 8,
    courseCount: 0,
    stores: [],
    newCards: [],
    oldCards: [],
    refunds: [],
    update_incomes: [],
    reservation_incomes: [],
    reservation_pays: [],
    update_pays: [],
    trialPayments: [],
    cardTicketIncomes: [],
    cardTicketRefunds: [],
    cardTicketRefundCount: 0,
    cardTicketRefundTotal: 0,
    voucherIncomes: [],
    voucherRefunds: [],
    voucherRefundCount: 0,
    voucherRefundTotal: 0,
    courses: [],
    teachers: [],
    courseStates,
    report: {
      store_id: '',
      store_name: '',
      report_date: '',
      new_income: 0,
      old_income: 0,
      trial_income: 0,
      card_ticket_income: 0,
      voucher_income: 0,
      reservation_income: 0,
      other_income: 0,
      other_incomes: [],

      cash_income: 0,
      card_income: 0,
      you_zan_income: 0,
      dou_yin_income: 0,
      transfer_income: 0,
      applet_income: 0,
      we_chat_income: 0,
      alipay_income: 0,
      meituan_income: 0,
      fu_zhang_gui_income: 0,

      all_income: 0,
      initial_cash: 0,
      expenditure: 0,
      cash_surplus: 0,
      // 备用金账：期初 + 新增 - 支出 = 期末，期末自动结转到次日
      reserve_income: 0,
      reserve_balance: 0,
      // 新增备用金只有「店长转入」一种，固定一行
      reserve_incomes: [{ amount: '', remark: '店长转入' }],
      reserve_pays: [],
      new_count: 0,
      old_count: 0,
      trial_count: 0,
      card_ticket_income_count: 0,
      voucher_income_count: 0,
      reservation_income_count: 0,
      group_course_count: 0,
      refund_count: 0,
      total_attendance: 0,
      average_attendance: 0,
      course_consumption: 0,
      our_store_consumption: 0,
      our_store_other_consumption: 0,
      other_store_consumption: 0,
      profit_consumption: 0,
      refund_pay: 0,
      refund_pay_input_error: 0,
      ticket_refund_pay: 0,
      ticket_refund_count: 0,
      update_income: 0,
      update_income_count: 0,
      update_pay: 0,
      update_pay_count: 0,
      reservation_pay: 0,
      reservation_pay_count: 0,
    },
    initial_income: {
      other_income: 0,
      cash_income: 0,
      card_income: 0,
      you_zan_income: 0,
      dou_yin_income: 0,
      transfer_income: 0,
      applet_income: 0,
      we_chat_income: 0,
      alipay_income: 0,
      meituan_income: 0,
      fu_zhang_gui_income: 0,
    },
  }),
  computed: {
    ...mapGetters([
      'isAdministrator',
    ]),
    id() {
      return +this.$route.params.id || 0;
    },
    courseEmptyText() {
      return this.isCardsLoading ? '正在加载…' : '当天没有课程安排';
    },
    // 供 ReportSections 渲染的明细数据集合
    list() {
      return {
        newCards: this.newCards,
        oldCards: this.oldCards,
        trialPayments: this.trialPayments,
        cardTicketIncomes: this.cardTicketIncomes,
        voucherIncomes: this.voucherIncomes,
        updateIncomes: this.update_incomes,
        reservationIncomes: this.reservation_incomes,
        refunds: this.refunds,
        cardTicketRefunds: this.cardTicketRefunds,
        voucherRefunds: this.voucherRefunds,
        reservationPays: this.reservation_pays,
        updatePays: this.update_pays,
      };
    },
    refundTotals() {
      return {
        cardTicketCount: this.cardTicketRefundCount,
        cardTicketTotal: this.cardTicketRefundTotal,
        voucherCount: this.voucherRefundCount,
        voucherTotal: this.voucherRefundTotal,
      };
    },
    // 支出合计只算退款类业务支出；备用金支出在备用金账内单独体现
    // 单位提醒：refund_pay / ticket_refund_pay 存的是分，reservation_pay / update_pay 是元
    expenseTotal() {
      const { report } = this;
      return plus(
        toYuan(report.refund_pay || 0),
        toYuan(report.ticket_refund_pay || 0),
        report.reservation_pay || 0,
        report.update_pay || 0,
      );
    },
    // 其他收入分组同理，按「分」展示
    otherIncomeCents() {
      return toCent(this.report.other_income || 0);
    },
    // 统计条的「总支出」= 退款类支出 + 备用金支出
    summaryExpenseTotal() {
      return plus(this.expenseTotal, this.report.expenditure || 0);
    },
    // ReportSections 统一按「分 + toYuan」渲染，这里把页面里已经是元的金额换算回分
    sectionsReport() {
      const r = this.report;
      return {
        ...r,
        new_income: toCent(r.new_income),
        old_income: toCent(r.old_income),
        trial_income: toCent(r.trial_income),
        card_ticket_income: toCent(r.card_ticket_income),
        voucher_income: toCent(r.voucher_income),
        update_income: toCent(r.update_income),
        reservation_income: toCent(r.reservation_income),
        reservation_pay: toCent(r.reservation_pay),
        update_pay: toCent(r.update_pay),
        all_income: toCent(r.all_income),
        cash_income: toCent(r.cash_income),
        card_income: toCent(r.card_income),
        you_zan_income: toCent(r.you_zan_income),
        dou_yin_income: toCent(r.dou_yin_income),
        transfer_income: toCent(r.transfer_income),
        applet_income: toCent(r.applet_income),
        we_chat_income: toCent(r.we_chat_income),
        alipay_income: toCent(r.alipay_income),
        meituan_income: toCent(r.meituan_income),
        fu_zhang_gui_income: toCent(r.fu_zhang_gui_income),
      };
    },
  },
  watch: {
    'report.report_date'() {
      this.handleReportDateAndStoreIdChange();
    },
    'report.store_id'() {
      this.handleReportDateAndStoreIdChange();
    },
    'report.new_income'() {
      this.computeAllIncome();
    },
    'report.old_income'() {
      this.computeAllIncome();
    },
    'report.trial_income'() {
      this.computeAllIncome();
    },
    'report.card_ticket_income'() {
      this.computeAllIncome();
    },
    'report.voucher_income'() {
      this.computeAllIncome();
    },
    'report.update_income'() {
      this.computeAllIncome();
    },
    'report.reservation_income'() {
      this.computeAllIncome();
    },
    'report.other_income'() {
      this.computeAllIncome();
    },
    'report.initial_cash'() {
      this.computeCashSurplus();
    },
    'report.expenditure'() {
      this.computeCashSurplus();
    },
    'report.cash_income'() {
      this.computeCashSurplus();
    },
  },
  methods: {
    findPaymentModeName,
    tableRowClassName({ row }) {
      return row.is_refund ? 'warning-background' : '';
    },
    computeCashSurplus() {
      const { report } = this;
      const num = minus(report.initial_cash, report.expenditure);
      report.cash_surplus = plus(num, report.cash_income);
    },
    // 备用金合计与期末由组件算好回传，避免页面再算一遍口径不一致
    handleReserveChange({ reserve_income, expenditure, reserve_balance }) {
      this.report.reserve_income = reserve_income;
      this.report.expenditure = expenditure;
      this.report.reserve_balance = reserve_balance;
    },
    // 分组内分页：按分类 key 转发到对应接口
    handlePage({ key, page }) {
      const handlers = {
        new: () => this.getDailyCards({ is_new: 1, page }),
        old: () => this.getDailyCards({ is_new: 0, page }),
        trial: () => this.getDailyTrailPayments({ page }),
        cardTicket: () => this.getCardTicketIncomes(page),
        voucher: () => this.getVoucherIncomes(page),
        reservationIncome: () => this.getReservationIncomes(page),
        cardTicketRefund: () => this.getCardTicketRefunds(page),
        voucherRefund: () => this.getVoucherRefunds(page),
        reservationPay: () => this.getReservationPays(page),
      };
      if (handlers[key]) handlers[key]();
    },
    // 把接口返回的备用金流水拆成「新增 / 支出」两组可编辑行
    normalizeReserveRecords(data) {
      const records = data.reserve_records || [];
      const toRows = type => records
        .filter(item => item.type === type)
        .map(item => ({ amount: toYuan(item.amount), remark: item.remark || '' }));
      const incomes = toRows(1).slice(0, 1);
      if (!incomes.length) incomes.push({ amount: '', remark: '店长转入' });
      const pays = toRows(2);
      // 旧数据只有合计、没有流水：转成一条待补备注的支出，避免保存时金额丢失
      if (!records.length && data.expenditure > 0) {
        pays.push({ amount: toYuan(data.expenditure), remark: '历史数据' });
      }
      return { incomes, pays };
    },
    computeAllIncome() {
      const { report } = this;
      report.all_income = plus(
        report.new_income,
        report.old_income,
        report.trial_income,
        report.card_ticket_income,
        report.voucher_income,
        report.update_income,
        report.reservation_income,
        report.other_income,
      );
    },
    removeOtherIncome(index) {
      this.report.other_incomes.splice(index, 1);
      this.computeOtherIncome();
    },
    computeOtherIncome() {
      const income = { ...this.initial_income };
      this.report.other_incomes.forEach(otherIncome => {
        const actualPayment = otherIncome.actual_payment;
        income.other_income = plus(income.other_income, actualPayment);

        if (otherIncome.card_payment_mode_id === paymentModes.card.value) return income.card_income = plus(income.card_income, actualPayment);
        if (otherIncome.card_payment_mode_id === paymentModes.cash.value) return income.cash_income = plus(income.cash_income, actualPayment);
        if (otherIncome.card_payment_mode_id === paymentModes.youZan.value) return income.you_zan_income = plus(income.you_zan_income, actualPayment);
        if (otherIncome.card_payment_mode_id === paymentModes.douYin.value) return income.dou_yin_income = plus(income.dou_yin_income, actualPayment);
        if (otherIncome.card_payment_mode_id === paymentModes.transfer.value) return income.transfer_income = plus(income.transfer_income, actualPayment);
        if (otherIncome.card_payment_mode_id === paymentModes.applet.value) return income.applet_income = plus(income.applet_income, actualPayment);
        if (otherIncome.card_payment_mode_id === paymentModes.weChat.value) return income.we_chat_income = plus(income.we_chat_income, actualPayment);
        if (otherIncome.card_payment_mode_id === paymentModes.alipay.value) return income.alipay_income = plus(income.alipay_income, actualPayment);
        if (otherIncome.card_payment_mode_id === paymentModes.meituan.value) return income.meituan_income = plus(income.meituan_income, actualPayment);
        if (otherIncome.card_payment_mode_id === paymentModes.fuZhangGui.value) return income.fu_zhang_gui_income = plus(income.fu_zhang_gui_income, actualPayment);
      });
      Object.assign(this.report, income);
    },
    addOtherIncome() {
      this.report.other_incomes.push({
        title: '',
        actual_payment: '',
      });
    },
    handleSubmit() {
      if (this.isSaving) return;

      this.$refs.form.validate(async (isSuccess) => {
        if (!isSuccess) return;

        const { ...postData } = this.report;
        // 备用金以一份扁平流水提交，页面上的两个数组只是编辑态
        const toReserveRecord = (rows, type) => rows
          .filter(item => Number(item.amount) > 0)
          .map(item => ({
            type,
            amount: toCent(item.amount || 0),
            remark: (item.remark || '').trim(),
          }));
        postData.reserve_records = [
          // 新增备用金最多一项，备注固定「店长转入」
          ...toReserveRecord(postData.reserve_incomes, 1)
            .map(item => ({ ...item, remark: item.remark || '店长转入' })),
          ...toReserveRecord(postData.reserve_pays, 2),
        ];
        delete postData.reserve_incomes;
        delete postData.reserve_pays;
        postData.new_income = toCent(postData.new_income);
        postData.old_income = toCent(postData.old_income);
        postData.trial_income = toCent(postData.trial_income);
        postData.card_ticket_income = toCent(postData.card_ticket_income);
        postData.voucher_income = toCent(postData.voucher_income);
        postData.update_income = toCent(postData.update_income);
        postData.reservation_income = toCent(postData.reservation_income);
        postData.other_income = toCent(postData.other_income);

        postData.cash_income = toCent(postData.cash_income);
        postData.card_income = toCent(postData.card_income);
        postData.you_zan_income = toCent(postData.you_zan_income);
        postData.dou_yin_income = toCent(postData.dou_yin_income);
        postData.transfer_income = toCent(postData.transfer_income);
        postData.applet_income = toCent(postData.applet_income);
        postData.we_chat_income = toCent(postData.we_chat_income);
        postData.alipay_income = toCent(postData.alipay_income);
        postData.meituan_income = toCent(postData.meituan_income);
        postData.fu_zhang_gui_income = toCent(postData.fu_zhang_gui_income);

        postData.all_income = toCent(postData.all_income);
        postData.initial_cash = toCent(postData.initial_cash);
        postData.expenditure = toCent(postData.expenditure);
        postData.cash_surplus = toCent(postData.cash_surplus);
        postData.update_pay = toCent(postData.update_pay);
        postData.reservation_pay = toCent(postData.reservation_pay);

        const teachers = [];
        this.courses.forEach(v => {
          v.teachers.forEach(teacher => {
            teachers.push({
              course_id: v.id,
              teacher_id: teacher.id,
              sub_teacher_id: teacher.sub_teacher_id,
              course_state: teacher.course_state,
              sub_course_state: teacher.sub_course_state,
            })
          })
        });
        postData.course_teachers = teachers;
        postData.other_incomes.forEach(otherIncome => {
          otherIncome.actual_payment = toCent(otherIncome.actual_payment);
        });

        this.isSaving = true;
        const { isError } = await update(this.id, postData);
        this.isSaving = false;
        if (isError) return;

        this.$message.success('日总结已保存');
        sessionStorage.setItem('daily_report_expand_id', this.id);
        RouteConfig.dailyReportList.back();
      });
    },
    async getTeachers() {
      const { isError, data } = await getTeachers({
        store_id: this.report.store_id,
        is_brief: 1,
      });

      if (isError) {
        this.isLoadingError = true;
        return;
      }

      this.teachers = data;
    },
    async getDailyCards({ is_new, page }) {
      const { isError, data } = await getDailyCards({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        is_new,
        page,
        size: this.cardListPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      if (is_new) {
        this.newCards = data.list;
        this.report.new_count = data.count;
      } else {
        this.oldCards = data.list;
        this.report.old_count = data.count;
      }
    },
    async getDailyTrailPayments({ page }) {
      const { isError, data } = await getDailyTrialPayments({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page,
        size: this.trailPaymentsPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.trialPayments = data.list;
      this.report.trial_count = data.count;
    },
    async getCardTicketIncomes(page) {
      const { isError, data } = await getCardTicketIncomes({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page,
        size: this.cardListPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.cardTicketIncomes = data.list || [];
      this.report.card_ticket_income_count = data.count || 0;
    },
    async getVoucherIncomes(page) {
      const { isError, data } = await getVoucherIncomes({
        store_id: this.report.store_id,
        // 编辑/查看页的 report_date 来自 show 接口，已是秒级时间戳，不能再转
        report_date: this.report.report_date,
        page,
        size: this.cardListPageSize,
      });

      if (isError) {
        this.isListLoadingError = true;
        return;
      }

      this.voucherIncomes = data.list || [];
      this.report.voucher_income_count = data.count || 0;
    },
    async getCardTicketRefunds(page) {
      const { isError, data } = await getCardTicketRefunds({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page,
        size: this.cardListPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.cardTicketRefunds = data.list || [];
      this.cardTicketRefundCount = data.count || 0;
      this.cardTicketRefundTotal = data.total || 0;
    },
    async getVoucherRefunds(page) {
      const { isError, data } = await getVoucherRefunds({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page,
        size: this.cardListPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.voucherRefunds = data.list || [];
      this.voucherRefundCount = data.count || 0;
      this.voucherRefundTotal = data.total || 0;
    },
    async getRefunds() {
      const { isError, data } = await getRefunds({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.refunds = data.list;
      this.report.refund_count = data.count;
      this.report.refund_pay = data.refund_pay || 0;
      this.report.refund_pay_input_error = data.refund_pay_input_error || 0;
    },
    async getUpdatePayments(is_income) {
      const { isError, data } = await getUpdatePayments({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        is_income,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      if (is_income) {
        this.update_incomes = data.list || [];
        this.report.update_income_count = data.count || 0;
      } else {
        this.update_pays = data.list || [];
        this.report.update_pay_count = data.count || 0;
        this.report.update_pay = toYuan(-data.total);
      }
    },
    async getReservationIncomes(page) {
      const { isError, data } = await getReservationIncomes({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page: page,
        size: this.listPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.report.reservation_income_count = data.count || 0;
      this.reservation_incomes = data.list || [];
    },
    async getReservationPays(page) {
      const { isError, data } = await getReservationPays({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page: page,
        size: this.listPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.report.reservation_pay_count = data.count || 0;
      this.reservation_pays = data.list || [];
    },
    async getIncomes() {
      const { isError, data } = await getIncomes({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      const cash_income = toYuan(data.cash_income);
      const card_income = toYuan(data.card_income);
      const you_zan_income = toYuan(data.you_zan_income);
      const dou_yin_income = toYuan(data.dou_yin_income);
      const transfer_income = toYuan(data.transfer_income);
      const applet_income = toYuan(data.applet_income);
      const we_chat_income = toYuan(data.we_chat_income);
      const alipay_income = toYuan(data.alipay_income);
      const meituan_income = toYuan(data.meituan_income);
      const fu_zhang_gui_income = toYuan(data.fu_zhang_gui_income);

      if (data.other_incomes) {
        data.other_incomes.forEach(otherIncome => {
          otherIncome.actual_payment = toYuan(otherIncome.actual_payment);
        });
      }

      Object.assign(this.report, {
        new_income: toYuan(data.new_income),
        old_income: toYuan(data.old_income),
        trial_income: toYuan(data.trial_income),
        card_ticket_income: toYuan(data.card_ticket_income),
        card_ticket_income_count: data.card_ticket_income_count || 0,
        voucher_income: toYuan(data.voucher_income),
        voucher_income_count: data.voucher_income_count || 0,
        // 券退款支出保持“分”，展示时由模板 toYuan 统一转换，不能再提前换算
        ticket_refund_pay: data.ticket_refund_pay,
        ticket_refund_count: data.ticket_refund_count || 0,
        update_income: toYuan(data.update_income),
        reservation_income: toYuan(data.reservation_income),
        other_income: 0,
        other_incomes: data.other_incomes || [],
        cash_income,
        card_income,
        you_zan_income,
        dou_yin_income,
        transfer_income,
        applet_income,
        we_chat_income,
        alipay_income,
        meituan_income,
        fu_zhang_gui_income,
        group_course_count: data.group_course_count || 0,
        total_attendance: data.total_attendance || 0,
        average_attendance: data.average_attendance || 0,
        course_consumption: data.course_consumption || 0,
        our_store_consumption: data.our_store_consumption || 0,
        other_store_consumption: data.other_store_consumption || 0,
        our_store_other_consumption: data.our_store_other_consumption || 0,
        profit_consumption: data.profit_consumption || 0,
        reservation_pay: toYuan(data.reservation_pay),
      });

      Object.assign(this.initial_income, {
        cash_income,
        card_income,
        you_zan_income,
        dou_yin_income,
        transfer_income,
        applet_income,
        we_chat_income,
        alipay_income,
        meituan_income,
        fu_zhang_gui_income,
      });
    },
    async getDailyCourses() {
      const { isError, data } = await getCourses({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      // 不能因为当天没有课程就提前 return：接口返回空数组时也要把列表清空，
      // 否则表格会残留上一次选择日期的课程，提交时这些旧课程还会被当作
      // 本次的 course_teachers 一并提交，写坏其他日期的课程出勤数据。
      // 如果此节课有代课老师，且代课老师不在老师列表中，则将其加入列表
      const { teachers } = this;
      for (let i = 0; i < data.length; i++) {
        let course = data[i];
        for (let j = 0; j < course.teachers.length; j++) {
          let teacher = course.teachers[j];
          if (!teacher.sub_teacher_id) continue;

          let isInTeachers = false;
          for (let k = 0; k < teachers.length; k++) {
            if (teachers[k].id === teacher.sub_teacher_id) isInTeachers = true;
          }
          if (isInTeachers) continue;

          teachers.unshift({
            id: teacher.sub_teacher_id,
            nickname: teacher.sub_nickname,
            avatar: teacher.sub_avatar,
          });
        }
      }

      this.courses = data;
    },
    getTableSummaries,
    async handleReportDateAndStoreIdChange() {
      if (this.isCardsLoading || !this.report.report_date) return;
      if (this.isAdministrator && !this.report.store_id) return;

      this.isCardsLoading = true;
      this.isCardsLoadingError = false;

      await Promise.all([
        this.getDailyCards({ is_new: 1 }),
        this.getDailyCards({ is_new: 0 }),
        this.getDailyTrailPayments({}),
        this.getCardTicketIncomes(1),
        this.getVoucherIncomes(1),
        this.getCardTicketRefunds(1),
        this.getVoucherRefunds(1),
        this.getRefunds(),
        this.getIncomes(),
        this.getDailyCourses(),
        this.getTeachers(),
        this.getUpdatePayments(1),
        this.getUpdatePayments(0),
        this.getReservationIncomes(1),
        this.getReservationPays(1),
      ]);

      this.isCardsLoading = false;
    },
    async show() {
      const { isError, data } = await show(this.id);
      if (isError) return;

      const reserve = this.normalizeReserveRecords(data);
      // 保存过的期初备用金以日总结上的值为准，不能被重新拉取的上日期末覆盖
      Object.assign(this.report, {
        store_id: data.store_id || 0,
        store_name: data.store_name || '',
        report_date: data.report_date || 0,
        initial_cash: toYuan(data.initial_cash),
        expenditure: toYuan(data.expenditure),
        reserve_income: toYuan(data.reserve_income || 0),
        reserve_balance: data.reserve_balance === null || data.reserve_balance === undefined
          ? toYuan(data.initial_cash) - toYuan(data.expenditure)
          : toYuan(data.reserve_balance),
        reserve_incomes: reserve.incomes,
        reserve_pays: reserve.pays,
      });

      await this.handleReportDateAndStoreIdChange();
      this.report.initial_cash = toYuan(data.initial_cash);
    },
  },
  beforeMount() {
    this.show();
  },
};
</script>

<style src="./report-card.css"></style>
<style src="./add.css" scoped/>
